import { useState, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { formatDate } from '@pb/shared';
import { createGroup, listGroups } from '../api/groups';
import { Dialog } from '../components/Dialog';
import { EmptyState, ErrorAlert, ErrorState, Loading } from '../components/States';
import { TagFilter, TagList } from '../components/Tags';
import { ThumbLink } from '../components/Thumb';
import { useProject } from '../lib/project';
import { anyPending } from '../lib/thumbs';
import { pluralize } from '../lib/text';
import { usePolling, useResource } from '../lib/useResource';

export function NameDescriptionDialog({
  title,
  submitLabel,
  initialName = '',
  initialDescription = '',
  onClose,
  onSubmit,
  children,
}: {
  title: string;
  submitLabel: string;
  initialName?: string;
  initialDescription?: string;
  onClose: () => void;
  onSubmit: (name: string, description: string) => Promise<void>;
  children?: ReactNode;
}) {
  const [name, setName] = useState(initialName);
  const [description, setDescription] = useState(initialDescription);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const submit = async () => {
    if (!name.trim()) return;
    setBusy(true);
    setError(null);
    try {
      await onSubmit(name.trim(), description);
    } catch (err) {
      setError(err);
      setBusy(false);
    }
  };

  return (
    <Dialog
      title={title}
      onClose={onClose}
      closeDisabled={busy}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button type="submit" form="name-description-form" className="btn btn-primary" disabled={busy || !name.trim()}>
            {busy ? 'Saving…' : submitLabel}
          </button>
        </>
      }
    >
      <form
        id="name-description-form"
        className="stack"
        onSubmit={(event) => {
          event.preventDefault();
          void submit();
        }}
      >
        <div className="field">
          <label htmlFor="nd-name">Name</label>
          <input
            id="nd-name"
            className="input"
            value={name}
            maxLength={200}
            onChange={(event) => setName(event.target.value)}
            required
          />
        </div>
        <div className="field">
          <label htmlFor="nd-description">Description</label>
          <textarea
            id="nd-description"
            className="textarea"
            value={description}
            maxLength={4000}
            onChange={(event) => setDescription(event.target.value)}
          />
        </div>
        {children}
      </form>
      <ErrorAlert error={error} />
    </Dialog>
  );
}

export function GroupsPage() {
  const { project } = useProject();
  const navigate = useNavigate();
  const [tagIds, setTagIds] = useState<number[]>([]);
  const [includeRetired, setIncludeRetired] = useState(false);
  const [search, setSearch] = useState('');
  const [creating, setCreating] = useState(false);
  const groups = useResource(
    () => listGroups(project.id, tagIds, includeRetired),
    [project.id, tagIds.join(','), includeRetired],
  );
  const all = groups.data ?? [];
  usePolling(anyPending(all.map((g) => g.thumbnail)), () => void groups.refresh());
  const needle = search.trim().toLowerCase();
  const list = needle
    ? all.filter((g) => g.name.toLowerCase().includes(needle) || g.description.toLowerCase().includes(needle))
    : all;

  return (
    <div className="section">
      <div className="page-header">
        <div className="page-title">
          <h2>Groups</h2>
          <p className="muted small">Ordered sets of slides that presentations reuse. Every presentation shows the latest version.</p>
        </div>
        <div className="page-actions">
          <button type="button" className="btn btn-primary" onClick={() => setCreating(true)}>
            New group
          </button>
        </div>
      </div>
      <div className="toolbar">
        <input
          type="search"
          className="input input-search"
          style={{ width: 320 }}
          placeholder="Filter by name"
          aria-label="Filter groups by name"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
        <label className="checkbox">
          <input type="checkbox" checked={includeRetired} onChange={(event) => setIncludeRetired(event.target.checked)} />
          Show retired
        </label>
      </div>
      <TagFilter selected={tagIds} onChange={setTagIds} />
      {groups.error ? (
        <ErrorState error={groups.error} onRetry={() => void groups.reload()} />
      ) : groups.data === undefined ? (
        <Loading label="Loading groups…" />
      ) : list.length === 0 ? (
        all.length === 0 && tagIds.length === 0 ? (
          <EmptyState
            title="No groups yet"
            action={
              <button type="button" className="btn btn-primary" onClick={() => setCreating(true)}>
                Create a group
              </button>
            }
          >
            Create a group here, or select slides in the library and add them to a new group.
          </EmptyState>
        ) : (
          <EmptyState title="No groups match">Change the filter or the tag selection.</EmptyState>
        )
      ) : (
        <div className="grid-cards">
          {list.map((g) => (
            <article key={g.id} className="tile">
              <ThumbLink
                to={`/projects/${project.id}/groups/${g.id}`}
                thumb={g.thumbnail}
                alt={g.name}
                badge={pluralize(g.slide_count, 'slide')}
                onRetried={() => void groups.refresh()}
              />
              <Link to={`/projects/${project.id}/groups/${g.id}`} className="tile-title" title={g.name}>
                {g.name}
              </Link>
              <div className="row-wrap">
                {g.retired ? <span className="badge badge-warning">Retired</span> : null}
                <span className="xsmall faint">
                  v{g.version_no} · {pluralize(g.page_count, 'page')} · {formatDate(g.updated_at)}
                </span>
              </div>
              {g.description ? <p className="small muted">{g.description}</p> : null}
              <TagList tags={g.tags} />
            </article>
          ))}
        </div>
      )}
      {creating ? (
        <NameDescriptionDialog
          title="New group"
          submitLabel="Create group"
          onClose={() => setCreating(false)}
          onSubmit={async (name, description) => {
            const created = await createGroup(project.id, { name, description });
            navigate(`/projects/${project.id}/groups/${created.id}`);
          }}
        />
      ) : null}
    </div>
  );
}
